import { v4 as uuidv4 } from "uuid";
import { ActivityChangeType } from "@/components/ActivityStream/ActivityStreamItem";
import { useSaveFile } from "@/hooks/useSaveFile";
import { useTypedSelector } from "@/store/store";
import { useAiActionStore, AI_AGENT } from "@/store/zustand/aiActionStore";
import {
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import {
  ChangeApplyStatus,
  Message,
  MessageContext,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { useMCPServers } from "@/hooks/useMCPServers";
import {
  useLoadCodebase,
  useRunTerminalCommand,
  useStartServer,
} from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import { ChatMessageOptions, PartialKey, UploadedFile } from "@/utils/types";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  useCanvasId,
  withTimeout,
} from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { cloneDeep, groupBy, merge, pick, throttle } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { applyTextEditorToContents } from "./useApplyChatBlock";
import { getFilesFromTree } from "./utils";
import {
  AssistantInstruction,
  AssistantInstructionError,
  parseAssistantMessage,
  parseMessageContentBlocks,
  TracedAssistantInstruction,
  useCreateMessageForChat,
} from "./utils/messages";
import { useExecuteDbCommand, usePreloadStoryboard } from "./utils/sideEffects";
import { useChatWithAI, PendingToolResult } from "@/utils/ai/chatWithAI";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { toast } from "sonner";
import { usePendingQuestions } from "./contexts/PendingQuestionsContext";

// Constants
const PERSIST_MESSAGE_THROTTLE_MS = 2000;
const PRELOAD_STORYBOARD_THROTTLE_MS = 400;

// TODO: Refactor out the message context properties like "hadError" and
// "savedActivityId" it is unclear what they are for

// Handles updating the status of messages to update the UI if needed and
// tracking the states in posthog
export const useUpdateMessageContext = () => {
  const setChatMessageContext = useAiActionStore(
    (state) => state.setChatMessageContext,
  );

  const updateMessageContext = (
    message: Message,
    updates: Partial<MessageContext>,
  ) => {
    const newMessageContext = {
      ...message.message_context,
      ...updates,
    };
    message.message_context = newMessageContext;
    setChatMessageContext(message.id, newMessageContext);
  };

  const updateApplyStatus = (
    message: Message,
    updates: Partial<MessageContext["changeApplyStatus"]>,
  ) => {
    updateMessageContext(message, {
      changeApplyStatus: merge(
        cloneDeep(message.message_context?.changeApplyStatus),
        updates,
      ),
    });
  };

  const updateMessageStatus = (
    message: Message,
    tracedInstruction: TracedAssistantInstruction,
    applyStatusWithDebug: PartialKey<ChangeApplyStatus, "fixing"> & {
      debug?: string;
    },
  ) => {
    const { traceId, ...instruction } = tracedInstruction;
    const { debug, ...applyStatusPartial } = applyStatusWithDebug;

    if (applyStatusPartial.status === "failed" && !applyStatusPartial.fixing) {
      applyStatusPartial.fixing = false;
    }
    const applyStatus = applyStatusPartial as ChangeApplyStatus;

    let updatedUI = true;
    switch (instruction.type) {
      case "fileChange":
        updateApplyStatus(message, {
          fileChanges: { [instruction.filepath]: applyStatus },
        });
        break;
      case "newStoryboard":
        if (instruction.filepath) {
          updateApplyStatus(message, {
            fileChanges: { [instruction.filepath]: applyStatus },
          });
        }
        break;
      case "newFile":
        updateApplyStatus(message, {
          fileChanges: { [instruction.filepath]: applyStatus },
        });
        break;
      case "sqlMigration":
        updateApplyStatus(message, { runSqlMigration: applyStatus });
        if (applyStatus.status === "succeeded") {
          updateMessageContext(message, { savedActivityId: "RAN_COMMAND" });
        }
        break;
      case "edgeFunctionDeployment":
        updateApplyStatus(message, { deployEdgeFunction: applyStatus });
        if (applyStatus.status === "succeeded") {
          updateMessageContext(message, { savedActivityId: "RAN_COMMAND" });
        }
        break;
      case "convexSync":
        updateApplyStatus(message, { runConvexSync: applyStatus });
        break;
      case "terminalCommand":
        updateApplyStatus(message, {
          terminalCommands: { [instruction.command]: applyStatus },
        });
        if (applyStatus.status === "succeeded") {
          updateMessageContext(message, { savedActivityId: "RAN_COMMAND" });
        }

        break;
      default:
        updatedUI = false;
    }

    track("AI_CHAT_SIDE_EFFECT", {
      ...applyStatus,
      traceId,
      instruction,
      debug,
      updatedUI,
    });
  };

  return { updateMessageContext, updateMessageStatus, updateApplyStatus };
};

export const useSendMessage = () => {
  const canvasId = useCanvasId();
  const chatWithAI = useChatWithAI();
  const { createUserMessage, createAssistantMessage } =
    useCreateMessageForChat();
  const setChatStreamingMessages = useAiActionStore(
    (state) => state.setChatStreamingMessages,
  );
  const fileTree = useTypedSelector((state) => state.codebase.fileTree);
  const { runConvexSync, runSqlMigration, deployEdgeFunction } =
    useExecuteDbCommand();
  const { updateMessageContext, updateMessageStatus, updateApplyStatus } =
    useUpdateMessageContext();
  const loadCodebase = useLoadCodebase();
  const restartDevServer = useStartServer();
  const runTerminalCommand = useRunTerminalCommand();
  const { setNewLayout, layoutMode } = useLayoutStore((state) =>
    pick(state, ["setNewLayout", "layoutMode", "leftTab"]),
  );
  const { preloadStoryboard, getPreloadedStoryboards } = usePreloadStoryboard();
  const { session } = useSession();
  const { fetchServers: fetchMCPServers } = useMCPServers();

  const { setPendingQuestions, setOnSubmitAnswers } = usePendingQuestions();

  const stopAiTrigger = useAiActionStore((state) => state.stopAiTrigger);
  const abortController = useRef<AbortController>();
  const lastSavedContent = useRef<string>("");

  const lastMessageOptsRef = useRef<
    | (ChatMessageOptions & {
        messagesDoNotSummarize: Message[];
        messagesCanSummarize: Message[];
      })
    | null
  >(null);
  const lastUserMessageRef = useRef<Message | null>(null);
  const lastAiResponsesRef = useRef<Message[]>([]);

  useEffect(() => {
    return () => {
      lastMessageOptsRef.current = null;
      lastUserMessageRef.current = null;
      lastAiResponsesRef.current = [];
    };
  }, []);

  const throttledSaveMessage = useRef(
    throttle(
      async (messageToSave: Message) => {
        try {
          await persistMessagesToDb([messageToSave]);
        } catch (error) {
          track("AI_CHAT_WARNING", {
            warning: "Failed to update streaming message",
            data: {
              error: getAxiosErorrMessage(error),
              messageId: messageToSave.id,
            },
          });
        }
      },
      PERSIST_MESSAGE_THROTTLE_MS,
      { leading: false, trailing: true },
    ),
  );

  const handleIncompleteToolCallsAndSave = async (
    message: Message,
  ): Promise<void> => {
    // Parse content array
    let contentArray: any[];
    if (
      typeof message.content === "string" &&
      message.content.startsWith("[")
    ) {
      try {
        contentArray = JSON.parse(message.content);
      } catch {
        return; // Can't parse, no changes
      }
    } else if (Array.isArray(message.content)) {
      contentArray = [...message.content];
    } else {
      return;
    }

    // Filter out empty text blocks and mark in-progress tool calls as aborted
    const updatedContent = contentArray
      .map((block) => {
        if (
          block.type === "tool-call" &&
          (!block.status || block.status === "in-progress")
        ) {
          return {
            ...block,
            status: "aborted",
          };
        }
        return block;
      })
      .filter((block) => {
        // Remove empty text blocks that can cause API errors
        if (
          block.type === "text" &&
          (!block.text || block.text.trim() === "")
        ) {
          return false;
        }
        return true;
      });

    // If content is now empty after filtering, don't save an empty message
    if (updatedContent.length === 0) {
      track("AI_CHAT_WARNING", {
        warning: "Skipping save of empty assistant message after abort",
        data: { messageId: message.id },
      });
      return;
    }

    const messageToSave = createAssistantMessage({
      ...message,
      content: JSON.stringify(updatedContent),
    });

    // Save immediately instead of using throttled save for abort handling
    try {
      await persistMessagesToDb([messageToSave]);
    } catch (error) {
      track("AI_CHAT_WARNING", {
        warning: "Failed to persist aborted tool calls",
        data: {
          error: getAxiosErorrMessage(error),
          messageId: messageToSave.id,
        },
      });
    }
  };

  const handleIncrementalSave = (latestMessage: Message) => {
    if (latestMessage.id) {
      const contentChanged = lastSavedContent.current !== latestMessage.content;

      if (contentChanged) {
        lastSavedContent.current = latestMessage.content || "";
        const messageToSave = createAssistantMessage({
          ...latestMessage,
        });
        throttledSaveMessage.current(messageToSave);
      }
    }
  };

  useEffect(() => {
    const handleAbort = async () => {
      if (abortController.current) {
        abortController.current.abort();
        useAiActionStore.getState().resetStreamingTokenCount();

        // Handle the latest streaming message that might have in-progress tool calls
        const currentStreamingMessages =
          useAiActionStore.getState().chatStreamingMessages;
        if (currentStreamingMessages && currentStreamingMessages.length > 0) {
          const latestMessage =
            currentStreamingMessages[currentStreamingMessages.length - 1];
          if (latestMessage && latestMessage.role === "assistant") {
            await handleIncompleteToolCallsAndSave(latestMessage);
          }
        }

        // Clean up continuation state to prevent memory leaks
        setPendingQuestions(null);
        lastMessageOptsRef.current = null;
        lastUserMessageRef.current = null;
        lastAiResponsesRef.current = [];

        setChatStreamingMessages(null);
        resetChatToggles("all");
      }
    };

    handleAbort();
  }, [stopAiTrigger]);

  const saveFile = useSaveFile();

  const fileToContents = useCodebaseStore((state) => state.fileToContents);
  const getFileContents = useCodebaseStore((state) => state.getFileContents);

  const dispatch = useDispatch();
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const persistMessagesToDb = async (newMessages: Message[]) => {
    const client = useSupabaseStore.getState().supabaseClient;
    const onDbSnapshot = useSupabaseStore.getState().onDbSnapshot;

    if (!client) {
      return;
    }

    onDbSnapshot("aiChatMessages", {
      newItems: newMessages,
    });

    await client.from("ai_chat_messages").upsert(newMessages as any[], {
      onConflict: "id",
      ignoreDuplicates: false,
    });
  };

  const runTerminalInstruction = async (
    message: Message,
    instruction: AssistantInstruction,
  ) => {
    if (instruction.type !== "terminalCommand") {
      return;
    }
    updateMessageStatus(message, instruction, {
      status: "in_progress",
    });
    try {
      const { success, error } = await runTerminalCommand({
        command: instruction.command,
        abortController: abortController.current,
      });

      if (success) {
        updateMessageStatus(message, instruction, {
          status: "succeeded",
        });
      } else {
        updateMessageStatus(message, instruction, {
          status: "failed",
          reason: `Terminal command failed`,
          debug: error,
        });
      }
      return { success, error };
    } catch (e) {
      updateMessageStatus(message, instruction, {
        status: "failed",
        reason: `Terminal command failed`,
        debug: getAxiosErorrMessage(e),
      });
      return { success: false, error: getAxiosErorrMessage(e) };
    }
  };

  const getFileContentsWithTimeout = async (filepath: string) => {
    const supabaseJWT = await getUnexpiredToken(session);
    try {
      return withTimeout(
        () => getFileContents({ canvasId, filename: filepath, supabaseJWT }),
        15 * 1000,
      );
    } catch (e) {
      track("AI_CHAT_ERROR", {
        error: `Timed out getting updated file contents`,
        data: filepath,
      });
      return undefined;
    }
  };

  const applyFileChange = async ({
    message,
    fileChange,
    filesInContext,
    contents,
    userPrompt,
    traceId,
    canRetry = true,
  }: {
    message: Message;
    fileChange: AssistantInstruction;
    userPrompt: string;
    filesInContext?: string[];
    contents: string;
    traceId?: string;
    canRetry?: boolean;
  }): Promise<
    | { success: true; newContents: string }
    | { success: false; error?: AssistantInstructionError }
  > => {
    track("AI_CHAT_PERFORMANCE", {
      task: "useSendMessage",
      event: "applyFileChange start",
      traceId,
    });

    const applyFileChangeStart = performance.now();
    if (fileChange.type !== "fileChange") {
      return { success: true, newContents: "" }; // Shouldn't ever happen if this function is called properly
    }

    const codebaseFiles = getFilesFromTree(fileTree, canvasId);
    updateMessageStatus(message, fileChange, { status: "in_progress" });

    // We don't trust any changes to files the AI never saw
    if (
      codebaseFiles?.includes(fileChange.filepath) &&
      !filesInContext?.includes(fileChange.filepath)
    ) {
      const tracedFileChange = {
        ...fileChange,
        filesInContext,
        traceId,
      };
      updateMessageStatus(message, tracedFileChange, {
        status: "failed",
        reason: "Blind edit",
        debug: "File was changed but not in context",
        fixing: canRetry,
      });
      return {
        success: false,
        error: { type: "blindEdit", filename: fileChange.filepath },
      };
    }

    // We only warn for this since it wouldn't necessarily end up as an
    // incorrect result, ideally our AI wouldn't make this mistake though.
    if (
      fileChange.command === "create" &&
      codebaseFiles?.includes(fileChange.filepath)
    ) {
      track("AI_CHAT_WARNING", {
        warning: "Calling create on a file that already exists",
        data: pick(fileChange, ["filepath"]),
        traceId,
      });
    }
    const supabaseJWT = await getUnexpiredToken(session);
    const { newContents, succeeded, error } = await applyTextEditorToContents(
      fileChange,
      contents,
      canvasId,
      supabaseJWT || "",
      {
        useRelaceAsFallback: true,
        originalUserPrompt: userPrompt,
        aiChatTraceId: traceId,
      },
    );

    track("AI_CHAT_PERFORMANCE", {
      task: "useSendMessage",
      event: "applyFileChange complete",
      durationMs: Math.round(performance.now() - applyFileChangeStart),
      traceId,
    });

    if (!succeeded) {
      updateMessageStatus(message, fileChange, {
        status: "failed",
        reason: error || "Unknown error",
        fixing: canRetry,
      });
      return {
        success: false,
        error: {
          type: "textEditorError",
          filename: fileChange.filepath,
          error: error || "Unknown error",
        },
      };
    }
    return { success: true, newContents };
  };

  const isChatLoading = useIsChatLoading();

  const resetChatToggles = useAiContextStore((state) => state.resetToggles);

  // Collect the output of the AI, updating the chat view as it streams in
  const sendMessage = async ({
    updateChat = true, // false is used for when running retries to fix existing broken messages
    retryCount = 0,
    refreshAfterResponse = false,
    sendChatMessageFiles,
    canRetry = true, // If this is true message context may be updated with fixing: true
    ...messageOpts
  }: ChatMessageOptions & {
    retryCount?: number;
    updateChat?: boolean;
    refreshAfterResponse?: boolean;
    sendChatMessageFiles?: UploadedFile[];
    canRetry?: boolean;
  }): Promise<Message[]> => {
    if (isChatLoading) {
      track("AI_CHAT_ERROR", {
        error: "sendMessage call while chat is loading",
      });
      return [];
    }

    let traceId: string | undefined = undefined;
    const sendMessageStart = performance.now();
    let perfCheckpoint = performance.now();
    const trackPerfCheckpoint = (
      event: string,
      metadata?: Record<string, any>,
    ) => {
      const now = performance.now();
      track("AI_CHAT_PERFORMANCE", {
        task: "useSendMessage",
        event,
        durationMs: Math.round(now - perfCheckpoint),
        traceId,
        ...(metadata && { metadata }),
      });
      perfCheckpoint = now;
    };

    // The old version of sendMessage took this parameter but treated
    // every value as if it was an image in how it used it in the prompt.
    // TODO: Validate that every use of sendChatMessageFiles is actually an
    // image and then rename everything to just use messageOpts.attachedFiles
    if (sendChatMessageFiles && sendChatMessageFiles.length > 0) {
      messageOpts.attachedFiles = (messageOpts.attachedFiles || []).concat(
        sendChatMessageFiles,
      );
    }

    const userMessage = createUserMessage(messageOpts);

    if (updateChat) {
      // Updates the UI
      setChatStreamingMessages([
        ...messageOpts.messagesCanSummarize,
        ...messageOpts.messagesDoNotSummarize,
        userMessage,
      ]);

      await persistMessagesToDb([userMessage]);
    }

    abortController.current = new AbortController();

    const handledMessageSegments = new Set<string>();
    const preprocessStreamedMessage = (message: Message) =>
      throttle(
        () => {
          const newChangeSegments = parseMessageContentBlocks(message)
            .filter((s) => !s.jsonIncomplete)
            .filter((s) => s.type === "aiChanges" && s.aiChanges?.changes)
            .filter((_, i) => {
              if (!handledMessageSegments.has(`${message.id}-${i}`)) {
                handledMessageSegments.add(`${message.id}-${i}`);
                return true;
              }
              return false;
            });

          for (const aiChangeSegment of newChangeSegments) {
            const { changes } = aiChangeSegment.aiChanges || { changes: [] };
            for (const aiChange of changes) {
              if (aiChange.storyboardName) {
                preloadStoryboard({
                  storyboardName: aiChange.storyboardName,
                  width: aiChange.size.width,
                  height: aiChange.size.height,
                  x: aiChange.topLeftCorner.x,
                  y: aiChange.topLeftCorner.y,
                });
              }
            }
          }
        },
        PRELOAD_STORYBOARD_THROTTLE_MS,
        // trailing: false to avoid weird timing issues, this function should only
        // be optimistic and not be guaranteed to run
        { leading: true, trailing: false },
      );

    // Get the entire response from the AI
    trackPerfCheckpoint("before chatWithAI");
    const {
      newMessages,
      error: chatWithAIError,
      filesInContext,
      traceId: newTraceId,
      requestPath,
      promptsConsumed,
      toolCallsCount,
      inputTokens,
      outputTokens,
      pendingUserInput,
    } = await chatWithAI({
      ...messageOpts,
      abortController: abortController.current,
      onStreamedMessages: (aiMessages: Message[]) => {
        if (updateChat) {
          // Updates the UI
          setChatStreamingMessages([
            ...messageOpts.messagesCanSummarize,
            ...messageOpts.messagesDoNotSummarize,
            userMessage,
            ...aiMessages,
          ]);

          const latestMessage = aiMessages[aiMessages.length - 1];
          if (!latestMessage) {
            return;
          }
          preprocessStreamedMessage(latestMessage);

          // For aiChatMaxAgent, incrementally update the message in database for any complete content
          if (messageOpts.agentChoice === AI_AGENT.MAX_AGENT) {
            handleIncrementalSave(latestMessage);
          }
        }
      },
    });
    traceId = newTraceId;

    // Find the last assistant message index to add metrics there (not the first)
    const lastAssistantIdx = (newMessages || []).reduce(
      (lastIdx, msg, idx) => (msg.role === "assistant" ? idx : lastIdx),
      -1,
    );

    const aiResponses = (newMessages || []).map((message, index) => {
      // Add usage data to the LAST assistant message (not the first)
      // If there's pending user input (askUserQuestionsTool), store as pendingMetrics
      // so they can be aggregated with continuation metrics later
      const messageContext =
        index === lastAssistantIdx
          ? pendingUserInput
            ? {
                ...message.message_context,
                pendingMetrics: {
                  promptsConsumed,
                  toolCallsCount,
                  inputTokens,
                  outputTokens,
                },
              }
            : {
                ...message.message_context,
                promptsConsumed,
                toolCallsCount,
                inputTokens,
                outputTokens,
              }
          : message.message_context;

      return createAssistantMessage({
        ...message,
        message_context: messageContext,
      });
    });

    if (session) refreshPlanInfo(session);

    if (pendingUserInput && updateChat) {
      lastMessageOptsRef.current = messageOpts;
      lastUserMessageRef.current = userMessage;
      lastAiResponsesRef.current = aiResponses;
      setPendingQuestions(pendingUserInput);

      const cleanupContinuationState = () => {
        setPendingQuestions(null);
        lastMessageOptsRef.current = null;
        lastUserMessageRef.current = null;
        lastAiResponsesRef.current = [];
        setChatStreamingMessages(null);
        resetChatToggles("all");
        useAiActionStore.getState().resetStreamingTokenCount();
      };

      setOnSubmitAnswers(async (pendingToolResult: PendingToolResult) => {
        if (!lastMessageOptsRef.current || !lastUserMessageRef.current) return;

        // Clear pending questions since the user has now provided input
        setPendingQuestions(null);

        const conversationId = lastUserMessageRef.current.conversation_id;
        if (!conversationId) {
          console.error("No conversation_id for tool-result message");
          return;
        }

        abortController.current = new AbortController();

        const toolResultMessage: Partial<Message> = {
          id: uuidv4().toString(),
          role: "tool",
          content: JSON.stringify([
            {
              type: "tool-result",
              toolCallId: pendingToolResult.toolCallId,
              toolName: pendingToolResult.toolName,
              content: {
                status: "success",
                ...pendingToolResult.result,
              },
            },
          ]),
          conversation_id: conversationId,
          canvas_id: canvasId,
          created_at: new Date().toISOString(),
        };

        await persistMessagesToDb([toolResultMessage as Message]);

        try {
          const continuationResult = await chatWithAI({
            ...lastMessageOptsRef.current,
            messagesDoNotSummarize: [
              ...lastMessageOptsRef.current.messagesDoNotSummarize,
              lastUserMessageRef.current,
              ...lastAiResponsesRef.current,
              toolResultMessage as Message,
            ],
            userPrompt: "",
            abortController: abortController.current,
            onStreamedMessages: (aiMessages: Message[]) => {
              setChatStreamingMessages([
                ...lastMessageOptsRef.current!.messagesCanSummarize,
                ...lastMessageOptsRef.current!.messagesDoNotSummarize,
                lastUserMessageRef.current!,
                ...lastAiResponsesRef.current,
                ...aiMessages,
              ]);
            },
          });

          if (continuationResult.error) {
            toast.error(`Continuation failed: ${continuationResult.error}`);
            cleanupContinuationState();
            return;
          }

          if (!continuationResult.newMessages?.length) {
            cleanupContinuationState();
            return;
          }

          const lastAssistantMsg = [...lastAiResponsesRef.current]
            .reverse()
            .find((msg) => msg.role === "assistant");
          const pending = lastAssistantMsg?.message_context?.pendingMetrics;
          const aggregatedMetrics = {
            promptsConsumed:
              (pending?.promptsConsumed || 0) +
              (continuationResult.promptsConsumed || 0),
            toolCallsCount:
              (pending?.toolCallsCount || 0) +
              (continuationResult.toolCallsCount || 0),
            inputTokens:
              (pending?.inputTokens || 0) +
              (continuationResult.inputTokens || 0),
            outputTokens:
              (pending?.outputTokens || 0) +
              (continuationResult.outputTokens || 0),
          };

          const lastAssistantIndex = continuationResult.newMessages.reduce(
            (lastIdx, msg, idx) => (msg.role === "assistant" ? idx : lastIdx),
            -1,
          );

          const continuationMessages = continuationResult.newMessages.map(
            (msg, index) =>
              createAssistantMessage({
                ...msg,
                message_context:
                  index === lastAssistantIndex
                    ? continuationResult.pendingUserInput
                      ? {
                          ...msg.message_context,
                          pendingMetrics: aggregatedMetrics,
                        }
                      : { ...msg.message_context, ...aggregatedMetrics }
                    : msg.message_context,
              }),
          );

          await persistMessagesToDb(continuationMessages);

          if (continuationResult.pendingUserInput) {
            lastAiResponsesRef.current = [
              ...lastAiResponsesRef.current,
              ...continuationMessages,
            ];
            setPendingQuestions(continuationResult.pendingUserInput);
          } else {
            cleanupContinuationState();
          }
        } catch (error) {
          console.error("Continuation failed:", error);
          toast.error("Failed to submit answers. Please try again.");
          cleanupContinuationState();
        }
      });

      await persistMessagesToDb(aiResponses);
      return aiResponses;
    }

    let shouldReloadCodebase = false;
    let shouldRestartDevServer = false;
    let allErrors: AssistantInstructionError[] = [];

    trackPerfCheckpoint(`Call to chatWithAI complete (${requestPath})`);

    if (chatWithAIError) {
      toast.error(chatWithAIError);
    }

    for (const message of aiResponses) {
      if (chatWithAIError) {
        updateMessageContext(message, { hadError: true });
        continue;
      }

      trackPerfCheckpoint("Parsing assistant message", {
        messageId: message.id,
      });
      const { instructions, errors } = parseAssistantMessage(message, traceId);

      allErrors = allErrors.concat(errors); // Handle them at the end

      shouldReloadCodebase =
        shouldReloadCodebase ||
        instructions.some((i) => i.type === "reloadCodebase");
      shouldRestartDevServer =
        shouldRestartDevServer ||
        instructions.some((i) => i.type === "restartDevServer");

      const newStoryboards = instructions.filter(
        (i) => i.type === "newStoryboard",
      );
      const newFiles = instructions.filter((i) => i.type === "newFile");
      const fileChanges = instructions.filter((i) => i.type === "fileChange");
      const terminalCommands = instructions.filter(
        (i) => i.type === "terminalCommand",
      );
      const dbCommands = instructions.filter(
        (i) =>
          i.type === "sqlMigration" ||
          i.type === "edgeFunctionDeployment" ||
          i.type === "convexSync",
      );

      // Run terminal commands first so that any new packages are installed
      // prior to file changes so that as the file changes come in the user
      // is more likely to see valid results from their dev server
      if (terminalCommands.length > 0) {
        trackPerfCheckpoint(`Starting terminal commands`, {
          terminalCommandsLength: terminalCommands.length,
        });
        setNewLayout(LeftTab.AIChat, LayoutMode.Code);
        for (const terminalCommand of terminalCommands) {
          // Errors here might be retryable but that's handled through the "Fix with AI" mechanism detecting errors in the terminal
          await runTerminalInstruction(message, terminalCommand);
        }
        setNewLayout(LeftTab.AIChat, layoutMode);
        trackPerfCheckpoint("Terminal commands completed");
      }

      // Prepare all file changes in one object to save them all together to
      // avoid the devserver hot reloading and showing a bunch of errors
      const fileContentsForPath: Record<string, string> = {};

      // Use the preloaded storyboards to find the filenames for them
      const preloadedStoryboards = await getPreloadedStoryboards();
      for (const newStoryboard of newStoryboards) {
        updateMessageStatus(message, newStoryboard, { status: "in_progress" });
        const preloadedStoryboard =
          preloadedStoryboards.find(
            (s) => s.name === newStoryboard.storyboardName,
          ) || (await preloadStoryboard(newStoryboard, newStoryboard.contents));
        if (!preloadedStoryboard) {
          // Should never happen
          updateMessageStatus(message, newStoryboard, {
            status: "failed",
            reason: "Failed to load storyboard",
          });
          continue;
        }
        const filePath = preloadedStoryboard.fullFilePath!;
        newStoryboard.filepath = filePath;
        fileContentsForPath[filePath] = newStoryboard.contents;
      }

      for (const newFile of newFiles) {
        updateMessageStatus(message, newFile, { status: "in_progress" });
        fileContentsForPath[newFile.filepath] = newFile.content;
      }

      const fileChangesByFile = groupBy(fileChanges, (f) => f.filepath);
      const applyFileChangeSuccesses: AssistantInstruction[] = [];
      await Promise.all(
        Object.entries(fileChangesByFile).map(
          async ([filepath, fileChanges]) => {
            trackPerfCheckpoint("Starting getFileContentsWithTimeout", {
              filepath,
            });
            let contents = await getFileContentsWithTimeout(filepath);
            trackPerfCheckpoint("Finished getFileContentsWithTimeout", {
              filepath,
            });

            for (const fileChange of fileChanges) {
              // Contents _should_ be defined, but sometimes it isn't and we haven't figured out why yet
              if (
                !contents &&
                (fileChange.command === "create" ||
                  fileChange.command === "overwrite")
              ) {
                track("AI_CHAT_WARNING", {
                  warning:
                    "File contents not found for Create / Overwrite command",
                  data: {
                    ...pick(fileChange, ["command", "filepath"]),
                    contents,
                  },
                  traceId,
                });
                // Allow it through since it doesn't need to know
              } else if (!contents) {
                track("AI_CHAT_ERROR", {
                  error: `File contents not found`,
                  data: {
                    ...pick(fileChange, ["command", "filepath"]),
                    contents,
                  },
                  traceId,
                });
                // Disallow since it may wipe the old contents
                return;
              }

              const result = await applyFileChange({
                message,
                fileChange,
                filesInContext,
                contents,
                userPrompt: messageOpts.userPrompt,
                traceId: traceId || "",
                canRetry,
              });
              if (result.success) {
                fileContentsForPath[fileChange.filepath] = result.newContents;
                applyFileChangeSuccesses.push(fileChange);

                // Update the contents so that the next file change has the latest contents
                contents = result.newContents;
              } else if (result.error) {
                allErrors.push(result.error);
              }
            }
          },
        ),
      );

      trackPerfCheckpoint("File changes processing completed");

      // Save all the files at once
      if (Object.keys(fileContentsForPath).length > 0) {
        trackPerfCheckpoint(`Starting saving files`);
        try {
          const result = await saveFile({
            fileToContentsToSave: fileContentsForPath,
            saveActivityType: ActivityChangeType.AI_CHANGE,
          });
          updateMessageContext(message, {
            savedActivityId: result?.data?.activityId,
            editedFiles: Object.keys(fileContentsForPath),
          });
          [...applyFileChangeSuccesses, ...newFiles, ...newStoryboards].forEach(
            (i) => {
              updateMessageStatus(message, i, {
                status: "succeeded",
              });
            },
          );

          trackPerfCheckpoint("File save completed successfully");
        } catch (e) {
          trackPerfCheckpoint("File save failed");
          [...applyFileChangeSuccesses, ...newFiles, ...newStoryboards].forEach(
            (i) => {
              updateMessageStatus(message, i, {
                status: "failed",
                reason: "Failed to save files",
              });
            },
          );
          updateMessageContext(message, { hadError: true });
        }
      }

      if (refreshAfterResponse) {
        // Provide a small amount of time for the changes to compile
        setTimeout(() => {
          useIframePoolStore.getState().refreshAllStoryboards();
        }, 200);
      }

      // Run any database commands last since the latency matters less there compared to file changes
      if (dbCommands.length > 0) {
        trackPerfCheckpoint(
          `Starting database commands (${dbCommands.length})`,
        );
      }
      for (const dbCommand of dbCommands) {
        updateMessageStatus(message, dbCommand, { status: "in_progress" });
        try {
          let result;
          switch (dbCommand.type) {
            case "sqlMigration":
              result = await runSqlMigration(dbCommand.filename);
              break;
            case "edgeFunctionDeployment":
              result = await deployEdgeFunction(dbCommand.filename);
              break;
            case "convexSync":
              result = await runConvexSync();
              break;
          }

          if (result.success) {
            updateMessageStatus(message, dbCommand, {
              status: "succeeded",
            });
          } else {
            updateMessageStatus(message, dbCommand, {
              status: "failed",
              reason: `Error during ${dbCommand.type}: ${
                result.error || "Unknown error"
              }`,
              fixing: false,
            });
          }
        } catch (e) {
          updateMessageStatus(message, dbCommand, {
            status: "failed",
            reason: `Error during ${dbCommand.type}: ${getAxiosErorrMessage(
              e,
            )}`,
            fixing: false,
          });
          continue;
        }
      }
      if (dbCommands.length > 0) {
        trackPerfCheckpoint("Database commands completed");
      }
    }

    trackPerfCheckpoint("applyChanges complete");
    if (shouldReloadCodebase) {
      loadCodebase();
    }
    if (shouldRestartDevServer) {
      restartDevServer({
        forceRestart: true,
        doNotReloadCodebase: shouldReloadCodebase,
      });
    }

    // Handle any errors, retrying if needed (this will update the messages if updates are needed)
    trackPerfCheckpoint("before processAssistantErrors");
    await processAssistantErrors({
      ...messageOpts,
      userMessage,
      aiResponses,
      errors: allErrors,
      retryCount,
      refreshAfterResponse,
      traceId,
    });

    trackPerfCheckpoint("processAssistantErrors complete");

    if (updateChat) {
      throttledSaveMessage.current.cancel();
      await persistMessagesToDb(aiResponses);
      trackPerfCheckpoint("persistMessagesToDb complete");

      const applyStatuses = aiResponses.flatMap((m) => {
        return Object.values(m.message_context?.changeApplyStatus || {});
      });
      const pendingStatuses = applyStatuses.filter(
        (s) => s.status === "pending",
      );
      if (pendingStatuses.length > 0) {
        track("AI_CHAT_ERROR", {
          error: "Pending statuses remaining",
          data: {
            pendingStatuses,
          },
          traceId,
        });
      }
    }

    if (updateChat) {
      setChatStreamingMessages(null);
      resetChatToggles("all");
      useAiActionStore.getState().resetStreamingTokenCount();

      // Refresh MCP servers to sync any connection status changes
      // that may have occurred during tool fetching
      fetchMCPServers();
    }

    track("AI_CHAT_PERFORMANCE", {
      task: "useSendMessage",
      event: "sendMessage complete",
      durationMs: Math.round(performance.now() - sendMessageStart),
      traceId,
    });

    return aiResponses;
  };

  const processAssistantErrors = async ({
    userMessage,
    aiResponses,
    errors,
    retryCount,
    refreshAfterResponse,
    traceId,
    ...messageOpts
  }: ChatMessageOptions & {
    userMessage: Message;
    aiResponses: Message[];
    errors: AssistantInstructionError[];
    retryCount: number;
    refreshAfterResponse: boolean;
    traceId?: string;
  }) => {
    const fixStringsForRetry: string[] = []; // This being non-empty triggers a retry
    const filesWithErrors = new Set<string>();

    // We don't add any fixstrings for these errors to trigger retrying because
    // retrying an already too long request wouldn't work anyway
    const incompleteJsonErrors = errors.filter(
      (e) => e.type === "incompleteSegmentJson",
    );
    for (const error of incompleteJsonErrors) {
      track("AI_CHAT_WARNING", {
        warning: "Incomplete JSON for Segment",
        data: error.segment,
        traceId,
      });

      // We likely ran out in the middle of a file, can at least inform the user of what wasn't completed.
      if (error.filepath) {
        updateApplyStatus(error.message, {
          fileChanges: {
            [error.filepath]: {
              status: "failed",
              reason:
                "Maximum output token limit hit. Please try again with a smaller change or regenerate the code for the affected file by clicking the button.",
              fixing: false,
              isOutputTokenLimitHit: true,
              continuationPrompt: `Regenerate the changes for file ${error.filepath}`,
            },
          },
        });
      }
    }

    const packageJsonErrors = errors.filter(
      (e) => e.type === "packageJsonChange",
    );
    if (packageJsonErrors.length > 0) {
      fixStringsForRetry.push(`Editing package.json directly is not allowed. Please use terminal command to install the required libraries.

Use the appropriate package manager to install the required libraries.

For example, if the project is using npm, you can run the following command:
"npm install <package-name>"

Similarly, if the project is using pnpm, you can run the following command:
"pnpm add <package-name>"`);

      for (const error of packageJsonErrors) {
        filesWithErrors.add(error.filename);
      }
    }

    const textEditorErrors = errors.filter((e) => e.type === "textEditorError");
    const textEditorErrorStrings = Array.from(
      new Set(
        textEditorErrors.map((e) => {
          return `${e.filename}: ${e.error}`;
        }),
      ),
    );
    if (textEditorErrors.length > 0) {
      fixStringsForRetry.push(
        `There was a mistake in the output in the last message, remake the changes for the following sections:\n\nErrors: [\n${textEditorErrorStrings.join(
          "\n",
        )}\n]`,
      );
      for (const error of textEditorErrors) {
        filesWithErrors.add(error.filename);
      }
    }

    const brokenSegmentJsonErrors = errors.filter(
      (e) => e.type === "brokenSegmentJson",
    );
    if (brokenSegmentJsonErrors.length > 0) {
      const brokenSegments = brokenSegmentJsonErrors.map((e) => e.segment);
      fixStringsForRetry.push(
        `The JSON format for these entire sections could not be parsed: ${brokenSegments
          .map((s) => s.content)
          .join(", ")}`,
      );
    }

    const blindEditErrors = errors.filter((e) => e.type === "blindEdit");
    const blindEditedFiles = Array.from(
      new Set(blindEditErrors.map((e) => e.filename)),
    );
    if (blindEditErrors.length > 0) {
      fixStringsForRetry.push(
        `You have attempted to modify files that were not included in the context: [${blindEditedFiles.join(
          ", ",
        )}]. I am now explicitly including them in the context. Regenerate the code by accounting for the file contents provided in the context.`,
      );
      for (const error of blindEditErrors) {
        filesWithErrors.add(error.filename);
      }
    }

    // Nothing worth retrying
    if (fixStringsForRetry.length === 0) {
      return;
    }

    const fixString = fixStringsForRetry.join("\n\n");

    track("AI_PROMPT_FIX_DIFF", {
      prompt: messageOpts.userPrompt,
      filesWithErrors: Array.from(filesWithErrors),
      fixString,
      totalFiles: Object.keys(fileToContents).length,
      sendChatMessageFiles: userMessage.message_context?.files || [],
      instructionErrors: errors,
      messageIds: aiResponses.map((m) => m.id),
      retryCount,
      traceId,
    });

    const maxRetries = 1;
    if (retryCount >= maxRetries) {
      return;
    }

    let aiFixMessages: Message[] = [];
    try {
      aiFixMessages = await sendMessage({
        ...messageOpts,
        messagesCanSummarize: messageOpts.messagesCanSummarize,
        messagesDoNotSummarize: [
          ...messageOpts.messagesDoNotSummarize,
          userMessage,
          ...aiResponses,
        ],
        userPrompt: fixString,
        retryCount: retryCount + 1,
        canRetry: retryCount + 1 < maxRetries,
        refreshAfterResponse,
        isFreePrompt: Boolean(messageOpts.isFreePrompt),
        updateChat: false, // Don't update the chat this time, we'll handle the messages
        disableTextEditor: true, // Force the AI to give full file contents to give it more time to think

        // These should have already been done for the first attempt
        useSearch: false,
        useReasoning: false,
      });
    } catch (e) {
      track("AI_CHAT_ERROR", {
        error: `Failure during retry: ${getAxiosErorrMessage(e)}`,
        data: {
          error: e,
          messageOpts,
        },
        traceId,
      });
    }

    const fixFileChanges = aiFixMessages.reduce((acc, m) => {
      const fileChanges =
        m.message_context?.changeApplyStatus?.fileChanges || {};
      return { ...acc, ...fileChanges };
    }, {});

    const filesWithReflectedStatus = new Set<string>();

    // Updating existing status messages when possible
    for (const initialResponse of aiResponses) {
      const initialFileChanges =
        initialResponse.message_context?.changeApplyStatus?.fileChanges || {};
      for (const filename of Object.keys(initialFileChanges)) {
        if (fixFileChanges[filename]) {
          updateApplyStatus(initialResponse, {
            fileChanges: { [filename]: fixFileChanges[filename] },
          });
          filesWithReflectedStatus.add(filename);
        }
      }
    }

    // Some files may be new after the AI does its second attempt
    const newFileStatuses = Object.keys(fixFileChanges).filter(
      (filename) => !filesWithReflectedStatus.has(filename),
    );
    const lastMessage = aiResponses[aiResponses.length - 1];
    for (const filename of newFileStatuses) {
      updateApplyStatus(lastMessage, {
        fileChanges: { [filename]: fixFileChanges[filename] },
      });
    }
  };

  // Its non-intuitive that the request will be cancelled on closing the page,
  // so warn before they do (this can be removed once we move to 100% backend).
  const isSendingMessage = useRef(false);
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isSendingMessage.current) {
        event.preventDefault();
        // In modern browsers the specifics of the string will be ignored, it'll just say "Changes you made may not be saved."
        event.returnValue =
          "Leaving the page will cancel your AI request. Are you sure you want to leave?";
        return event.returnValue;
      }
    };

    // Try to cancel the request so that they don't necessarily lose a prompt
    const handleUnload = () => {
      if (isSendingMessage.current && abortController.current) {
        abortController.current.abort();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, []);

  const sendMessageWithWarning = async (
    ...args: Parameters<typeof sendMessage>
  ) => {
    isSendingMessage.current = true;
    try {
      return await sendMessage(...args);
    } finally {
      isSendingMessage.current = false;
    }
  };

  return { sendMessage: sendMessageWithWarning };
};
